import IMask, { InputMask } from 'imask';

export interface DateMaskOptions {
    lazy?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
}

/**
 * Creates an IMask instance for date input with the specified format.
 * Can be used standalone or within a larger preparer architecture.
 */
export function createDateMask(input: HTMLInputElement, format: string, options: DateMaskOptions = {}): InputMask<any> {
    const maskPattern = getMaskPattern(format);

    const mask = IMask(input, {
        mask: maskPattern,
        lazy: options.lazy ?? true,
        overwrite: true,
        blocks: {
            d: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 31,
                maxLength: 2,
                autofix: 'pad',
                placeholderChar: 'D',
            },
            m: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 12,
                maxLength: 2,
                autofix: 'pad',
                placeholderChar: 'M',
            },
            Y: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 9999,
                maxLength: 4,
                autofix: false,
                placeholderChar: 'Y',
            },
        },
    });

    const focusHandler = (): void => {
        mask.updateOptions({ lazy: false });
        options.onFocus?.();
    };

    const blurHandler = (): void => {
        if (!/\d/.test(input.value)) {
            mask.updateOptions({ lazy: true });
        }
        options.onBlur?.();
    };

    input.addEventListener('focus', focusHandler);
    input.addEventListener('blur', blurHandler);

    return mask;
}

function getMaskPattern(format: string): string {
    const lowerFormat = format.toLowerCase();
    if (lowerFormat.startsWith('d')) {
        return 'd/`m/`Y';
    }
    return 'm/`d/`Y';
}

export function fillCurrentDate(input: HTMLInputElement, format: string, mask: { value: string }): void {
    if (input.dataset['fillCurrentDate'] !== 'true' || input.value) {
        return;
    }

    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear()).padStart(4, '0');

    mask.value = format.toLowerCase().startsWith('d')
        ? `${day}/${month}/${year}`
        : `${month}/${day}/${year}`;
}

export interface DateFieldPreparerOptions {
    onFocus?: (event: FocusEvent) => void;
    onBlur?: (element: HTMLElement) => void;
    onChange?: () => void;
}

export class DateFieldPreparer {
    private mask: InputMask<any> | null = null;

    constructor(
        private readonly fieldElement: HTMLElement,
        private readonly options: DateFieldPreparerOptions = {}
    ) {}

    public prepare(): void {
        const inputElement = this.fieldElement.querySelector<HTMLInputElement>('input');
        if (!inputElement) {
            return;
        }

        const format = this.getDateFormat(inputElement);
        this.mask = createDateMask(inputElement, format);
        fillCurrentDate(inputElement, format, this.mask);
        this.prepareListeners(inputElement);
    }

    public destroy(): void {
        if (this.mask) {
            this.mask.destroy();
            this.mask = null;
        }
    }

    public updateValue(): void {
        if (this.mask) {
            this.mask.updateValue();
        }
    }

    private prepareListeners(input: HTMLInputElement): void {
        if (this.options.onFocus) {
            input.addEventListener('focus', (event: FocusEvent) => {
                this.options.onFocus?.(event);
            });
        }

        if (this.options.onBlur) {
            input.addEventListener('blur', () => {
                this.options.onBlur?.(this.fieldElement);
            });
        }

        if (this.options.onChange) {
            input.addEventListener('change', () => {
                this.options.onChange?.();
            });
        }
    }

    private getDateFormat(input: HTMLInputElement): string {
        // Try to get format from data-format attribute (new approach)
        const dataFormat = input.dataset['format'];
        if (dataFormat) {
            return dataFormat;
        }

        // Fallback to data-form-field-meta-info (legacy Base64 approach)
        const metaInfo = this.fieldElement.getAttribute('data-form-field-meta-info');
        if (metaInfo) {
            try {
                const decoded = JSON.parse(atob(metaInfo));
                if (decoded?.format) {
                    return decoded.format;
                }
            } catch {
                // Ignore parse errors
            }
        }

        // Default format
        return 'MM/dd/yyyy';
    }
}

/**
 * Initialize date fields in a container element
 */
export function initializeDateFields(container: HTMLElement, options: DateFieldPreparerOptions = {}): DateFieldPreparer[] {
    const dateFields = container.querySelectorAll<HTMLElement>('[data-element="field"][data-field="date"], [data-element="field"][data-field="birthday"]');
    const preparers: DateFieldPreparer[] = [];

    dateFields.forEach(field => {
        const preparer = new DateFieldPreparer(field, options);
        preparer.prepare();
        preparers.push(preparer);
    });

    return preparers;
}
